import { BracketSeries } from "@portal-frontend-ssr/blast-api";
import classNames from "classnames";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { SwissTournamentContext } from "../../../../SwissBracketComponent";
import { Tooltip } from "@portal-frontend-ssr/ui";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";

interface GroupMatchProps {
  match: BracketSeries;
  otherMatches: BracketSeries[];
}

const GroupMatch = ({ match, otherMatches }: GroupMatchProps) => {
  const swissContext = useContext(SwissTournamentContext);
  const enterTeamTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const exitTeamTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const onTeamEnter = useCallback(
    (teamUuid?: string) => {
      clearTimeout(exitTeamTimeoutRef.current);
      enterTeamTimeoutRef.current = setTimeout(() => {
        swissContext?.setHoveredTeamUuid(teamUuid);
      }, 150);
    },
    [swissContext],
  );

  const onTeamLeave = useCallback(() => {
    clearTimeout(enterTeamTimeoutRef.current);
    exitTeamTimeoutRef.current = setTimeout(() => {
      swissContext?.setHoveredTeamUuid(undefined);
    }, 100);
  }, [swissContext]);

  useEffect(() => {
    return () => {
      clearTimeout(enterTeamTimeoutRef.current);
      clearTimeout(exitTeamTimeoutRef.current);
    };
  }, []);

  const shouldHaveBorder = useMemo(() => {
    if (!swissContext?.hoveredTeamUuid) {
      return false;
    }
    return match.teamA?.uuid === swissContext?.hoveredTeamUuid || match.teamB?.uuid === swissContext?.hoveredTeamUuid;
  }, [swissContext?.hoveredTeamUuid, match.teamA?.uuid, match.teamB?.uuid]);

  const shouldLowerOpacity = useMemo(() => {
    if (!swissContext?.hoveredTeamUuid) return false;
    if (match.teamA?.uuid === swissContext?.hoveredTeamUuid) return false;
    if (match.teamB?.uuid === swissContext?.hoveredTeamUuid) return false;
    if (
      otherMatches.some((otherMatch) => {
        if (otherMatch.teamA?.uuid === swissContext?.hoveredTeamUuid) return false;
        if (otherMatch.teamB?.uuid === swissContext?.hoveredTeamUuid) return false;
        return true;
      })
    )
      return true;
    return false;
  }, [swissContext?.hoveredTeamUuid, match.teamA?.uuid, match.teamB?.uuid, otherMatches]);
  return (
    <div
      className={classNames(
        "not-last:mb-1 flex flex-row items-center justify-between gap-2 rounded border bg-background-100 p-1 px-2 transition-all duration-200 ease-in-out",
        {
          "border-primary-100": shouldHaveBorder,
          "border-transparent": !shouldHaveBorder,
          "opacity-50": shouldLowerOpacity,
        },
      )}
    >
      {match.teamA ? (
        <Tooltip content={match.teamA.name} enterDelay={150} exitDelay={100}>
          <div className="flex h-full items-center">
            <img
              className="relative size-5 object-scale-down align-middle"
              src={generateTeamLogoSrc({ id: match.teamA.uuid, height: 56 })}
              alt={match.teamA.name}
              onMouseEnter={() => onTeamEnter(match.teamA?.uuid)}
              onMouseLeave={() => onTeamLeave()}
            />
          </div>
        </Tooltip>
      ) : (
        <span className="flex size-5 place-content-center items-center rounded-full bg-background-90 text-center text-white font-style-label-l4">
          ?
        </span>
      )}
      <div className="flex w-12 flex-row place-content-center items-center justify-center rounded bg-background-90">
        {match.isCompleted && (
          <span
            className={classNames(
              "text-white font-style-label-l3",
              match.teamAScore > match.teamBScore && match.isCompleted ? "text-success" : "text-white",
            )}
          >
            {match.teamAScore}
          </span>
        )}
        <span className="py-1 text-center text-white font-style-label-l4">{match.isCompleted ? " : " : "VS"}</span>
        {match.isCompleted && (
          <div className="flex flex-row items-center justify-center">
            <span
              className={classNames(
                "text-white font-style-label-l3",
                match.teamBScore > match.teamAScore && match.isCompleted ? "text-success" : "text-white",
              )}
            >
              {match.teamBScore}
            </span>
          </div>
        )}
      </div>
      {match.teamB ? (
        <Tooltip content={match.teamB.name} enterDelay={150} exitDelay={100}>
          <div>
            <img
              className="relative max-h-5 w-5 object-scale-down align-middle"
              src={generateTeamLogoSrc({ id: match.teamB.uuid, height: 56 })}
              alt={match.teamB?.name}
              onMouseEnter={() => onTeamEnter(match.teamB?.uuid)}
              onMouseLeave={() => onTeamLeave()}
            />
          </div>
        </Tooltip>
      ) : (
        <span className="flex size-5 place-content-center items-center rounded-full bg-background-90 text-center text-white font-style-label-l4">
          ?
        </span>
      )}
    </div>
  );
};

export { GroupMatch };
